import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Ticket, TicketOrigin, SortDirection } from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { useBackNavigation } from '../../utils/navigation.js';
import { useRowLink } from '../../utils/useRowLink.js';
import { formatDate } from '../../utils/date.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import { TicketForecastPanel } from '../forecast/TicketForecastPanel.js';
import { MemberMultiFilter } from '../members/MemberMultiFilter.js';
import { FilterDefaultsButtons } from '../filters/FilterDefaultsButtons.js';
import { savedString, savedStringArray, useFilterDefaults } from '../filters/useFilterDefaults.js';
import { TicketFormModal } from './TicketFormModal.js';
import { useTrackRecent } from '../quick-access/useTrackRecent.js';
import { ticketStatusTone } from './status.js';
import { ticketDisplayId } from './reference.js';
import {
  useTicket,
  useTickets,
  useTicketStatusOptions,
  useTicketTypeOptions,
} from './api.js';

/** Column keys the tickets list can be sorted by (mirrors the backend sort allow-list). */
type TicketSortKey =
  | 'number'
  | 'title'
  | 'origin'
  | 'statusName'
  | 'ltt'
  | 'createdAt'
  | 'updatedAt'
  | 'closedAt';

type TicketSort = { key: TicketSortKey; dir: SortDirection };

/** A clickable table header that toggles server-side sorting for its column. */
function SortableHeader({
  label,
  sortKey,
  sort,
  onToggle,
  title,
}: {
  label: string;
  sortKey: TicketSortKey;
  sort: TicketSort;
  onToggle: (key: TicketSortKey) => void;
  title?: string;
}): JSX.Element {
  const active = sort.key === sortKey;
  const indicator = active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕';
  return (
    <th className="px-4 py-3 font-medium" title={title}>
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide ${
          active ? 'text-slate-700' : 'hover:text-slate-700'
        }`}
        aria-label={`Sort by ${title ?? label}`}
      >
        {label}
        <span className={`text-[10px] ${active ? 'text-slate-700' : 'text-slate-300'}`}>
          {indicator}
        </span>
      </button>
    </th>
  );
}

export function TicketsPage(): JSX.Element {
  const { can, canSeeMenu, isAdmin, principal } = useAuth();
  const backNav = useBackNavigation();
  const navigate = useNavigate();
  const rowLink = useRowLink();
  const currentMemberId = principal?.type === 'member' ? principal.id : '';

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [origin, setOrigin] = useState<'' | TicketOrigin>('');
  const [ticketTypeId, setTicketTypeId] = useState('');
  // Status filter: 'open' = any not-closed status (default), '' = all, otherwise a status id.
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [assigneeMemberIds, setAssigneeMemberIds] = useState<string[]>([]);
  const [sort, setSort] = useState<TicketSort>({ key: 'number', dir: 'asc' });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Ticket | null>(null);
  const [pendingOpenId, setPendingOpenId] = useState<string | null>(null);

  useTrackRecent(
    formOpen && editing
      ? {
          entity: 'ticket',
          id: editing.id,
          label: `${editing.referenceId ?? `#${editing.number}`} · ${editing.title}`,
          path: `/tickets/${editing.id}`,
        }
      : null,
  );
  const { id: routeTicketId } = useParams<{ id?: string }>();

  // Support and project tickets are gated independently by RBAC; a member only sees the
  // origins they hold access to. Admins see both.
  const canSupport = can('supportTickets');
  const canProject = can('projectTickets');

  // Deep-link (e.g. from the dashboard): ?open=<id> opens that ticket's modal.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      setPendingOpenId(openId);
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Follow the sidebar links (?origin=support|project). The Support/Project Tickets menu items
  // both render this page, so the filter has to react to param changes, not just the first mount.
  const originParamRef = useRef<string | null>(null);
  useEffect(() => {
    const o = searchParams.get('origin');
    if (o === originParamRef.current) return;
    originParamRef.current = o;
    const next: '' | TicketOrigin = o === 'support' || o === 'project' ? o : '';
    setOrigin(next);
    setPage(1);
    if (next === 'project') setTicketTypeId('');
  }, [searchParams]);

  // When a member can only see one origin, lock the filter to it (the backend enforces the
  // same restriction regardless of the client-side value).
  useEffect(() => {
    if (canSupport && !canProject && origin !== 'support') setOrigin('support');
    else if (!canSupport && canProject && origin !== 'project') setOrigin('project');
  }, [canSupport, canProject, origin]);

  // Route-based deep-link: /tickets/:id opens that ticket's standard modal.
  useEffect(() => {
    if (routeTicketId) {
      setPendingOpenId(routeTicketId);
    }
  }, [routeTicketId]);

  const pendingQuery = useTicket(pendingOpenId);
  useEffect(() => {
    if (pendingOpenId && pendingQuery.data) {
      setEditing(pendingQuery.data);
      setFormOpen(true);
      setPendingOpenId(null);
    }
  }, [pendingOpenId, pendingQuery.data]);

  const query = useTickets({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    search: search || undefined,
    customerId: customerId || undefined,
    origin: origin || undefined,
    ticketTypeId: ticketTypeId || undefined,
    open: statusFilter === 'open' ? true : undefined,
    statusId: statusFilter === 'open' || statusFilter === '' ? undefined : statusFilter,
    assigneeMemberIds: assigneeMemberIds.length > 0 ? assigneeMemberIds : undefined,
    sortBy: sort.key,
    sortDir: sort.dir,
  });
  const statusesQuery = useTicketStatusOptions();
  const typesQuery = useTicketTypeOptions();

  // Apply initial filter defaults once data is available: assignee = current member.
  // Status defaults to "Open" (see statusFilter initial state). Users can change or clear both.
  // A saved filter default (below) loads afterwards and overrides this seed.
  const didInitDefaults = useRef(false);
  useEffect(() => {
    if (didInitDefaults.current) {
      return;
    }
    if (!principal) {
      return;
    }
    didInitDefaults.current = true;
    if (currentMemberId) {
      setAssigneeMemberIds([currentMemberId]);
    }
  }, [principal, currentMemberId]);

  const filterDefaults = useFilterDefaults('tickets', (saved) => {
    didInitDefaults.current = true;
    const savedCustomer = savedString(saved, 'customerId');
    if (savedCustomer !== null) setCustomerId(savedCustomer);
    const savedType = savedString(saved, 'ticketTypeId');
    if (savedType !== null) setTicketTypeId(savedType);
    const savedStatus = savedString(saved, 'statusFilter');
    if (savedStatus !== null) setStatusFilter(savedStatus);
    const savedAssignees = savedStringArray(saved, 'assigneeMemberIds');
    if (savedAssignees !== null) setAssigneeMemberIds(savedAssignees);
  });

  const statusNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const status of statusesQuery.data ?? []) {
      map.set(status.id, status.name);
    }
    return map;
  }, [statusesQuery.data]);

  const types = typesQuery.data ?? [];
  const statuses = statusesQuery.data ?? [];

  const tickets = query.data?.data ?? [];
  const pageInfo = query.data?.page;
  const hasFilters = Boolean(
    search ||
      customerId ||
      origin ||
      ticketTypeId ||
      statusFilter !== 'open' ||
      assigneeMemberIds.length > 0,
  );

  const handleSearchSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const toggleSort = (key: TicketSortKey): void => {
    setPage(1);
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  };

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };

  // Closing returns the address bar to the list when the modal was opened via a deep link.
  const closeForm = (): void => {
    setFormOpen(false);
    setEditing(null);
    if (routeTicketId) navigate('/tickets');
  };

  return (
    <div>
      <PageHeader
        title="Tickets"
        description="Track support and project work, assign members, and log time."
        actions={
          <>
            {backNav.canGoBack ? (
              <Button variant="secondary" onClick={backNav.goBack}>
                ← {backNav.backLabel}
              </Button>
            ) : null}
            {origin === 'support' ? (
              <Button
                variant="secondary"
                onClick={() =>
                  window.open(
                    'https://portal.nscatalyst.com/create_ticket.php',
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
              >
                Create ticket
              </Button>
            ) : null}
            {isAdmin ? (
              <Button onClick={openCreate}>
                <Icon name="tickets" className="h-4 w-4" />
                New ticket
              </Button>
            ) : null}
          </>
        }
      />

      {canSeeMenu('forecast') ? (
        <TicketForecastPanel enabled={can('tickets')} defaultCollapsed />
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4">
          <form onSubmit={handleSearchSubmit} className="flex max-w-md flex-1 items-center gap-2">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by ticket title, customer, reference, or number…"
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>
          <div className="w-64">
            <CustomerSelectField
              value={customerId}
              onChange={(value) => {
                setPage(1);
                setCustomerId(value);
              }}
              placeholder="All customers"
              allowClear
            />
          </div>
          <select
            value={origin}
            onChange={(e) => {
              setPage(1);
              const value = e.target.value as '' | TicketOrigin;
              setOrigin(value);
              if (value === 'project') {
                setTicketTypeId('');
              }
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            {canSupport && canProject ? <option value="">All types</option> : null}
            {canSupport ? <option value="support">Support</option> : null}
            {canProject ? <option value="project">Project</option> : null}
          </select>
          {origin !== 'project' ? (
            <select
              value={ticketTypeId}
              onChange={(e) => {
                setPage(1);
                setTicketTypeId(e.target.value);
              }}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value="">All categories</option>
              {types.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          ) : null}
          <select
            value={statusFilter}
            onChange={(e) => {
              setPage(1);
              setStatusFilter(e.target.value);
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="open">Open</option>
            <option value="">All statuses</option>
            {statuses.map((status) => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>
          <MemberMultiFilter
            selected={assigneeMemberIds}
            onChange={(next) => {
              setPage(1);
              setAssigneeMemberIds(next);
            }}
            allLabel="All assignees"
          />
          <FilterDefaultsButtons
            handle={filterDefaults}
            onSave={() =>
              filterDefaults.save({ customerId, ticketTypeId, statusFilter, assigneeMemberIds })
            }
          />
        </div>

        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <SortableHeader label="Ticket" sortKey="number" sort={sort} onToggle={toggleSort} />
              <SortableHeader label="Title" sortKey="title" sort={sort} onToggle={toggleSort} />
              <th className="px-4 py-3 font-medium">Customer</th>
              <SortableHeader label="Type" sortKey="origin" sort={sort} onToggle={toggleSort} />
              <th className="px-4 py-3 font-medium">Category</th>
              <SortableHeader
                label="Status"
                sortKey="statusName"
                sort={sort}
                onToggle={toggleSort}
              />
              <SortableHeader
                label="LTT"
                sortKey="ltt"
                sort={sort}
                onToggle={toggleSort}
                title="Latest ticket time entry"
              />
              <SortableHeader
                label="Created"
                sortKey="createdAt"
                sort={sort}
                onToggle={toggleSort}
              />
              <SortableHeader
                label="Updated"
                sortKey="updatedAt"
                sort={sort}
                onToggle={toggleSort}
                title="Last updated"
              />
              <SortableHeader label="Closed" sortKey="closedAt" sort={sort} onToggle={toggleSort} />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={10}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={tickets.length === 0}
              emptyTitle="No tickets found"
              emptyDescription={
                hasFilters ? 'Try a different search or filter.' : 'No tickets to show.'
              }
              emptyAction={
                isAdmin && !hasFilters ? (
                  <Button onClick={openCreate}>New ticket</Button>
                ) : undefined
              }
            >
              {tickets.map((ticket) => (
                <tr
                  key={ticket.id}
                  className="cursor-pointer hover:bg-slate-50"
                  {...rowLink(`/tickets/${ticket.id}`)}
                >
                  <td className="px-4 py-3 tabular-nums text-slate-500">{ticketDisplayId(ticket)}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{ticket.title}</td>
                  <td className="px-4 py-3 text-slate-600">{ticket.customerName ?? '—'}</td>
                  <td className="px-4 py-3">
                    <Badge tone={ticket.origin === 'support' ? 'amber' : 'blue'}>
                      {ticket.origin === 'support' ? 'Support' : 'Project'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {ticket.origin === 'project'
                      ? (ticket.defaultServiceCategoryName ?? '—')
                      : (ticket.ticketTypeName ?? '—')}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {statusNames.get(ticket.statusId) ? (
                      <Badge tone={ticketStatusTone(statusNames.get(ticket.statusId))}>
                        {statusNames.get(ticket.statusId)!}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(ticket.latestTimeDate)}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(ticket.createdAt)}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(ticket.updatedAt)}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(ticket.closedAt)}</td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && tickets.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <TicketFormModal isOpen={formOpen} ticket={editing} onClose={closeForm} />
    </div>
  );
}
